import * as O from 'fp-ts/lib/Option';
import {Account} from 'Agent/Accounts/models/Account';
import {AccountReportRequest} from 'Agent/Accounts/models/AccountReport';
import {Async, AsyncPage, Empty} from 'Core/models/Async';
import {DateRange} from 'Core/models/DateRange';
import {FilterOptionUnion, FilterOptionValue} from 'Core/models/FilterOption';
import {Page} from 'Core/models/Page';
import {createAction} from 'Shared/utilities/actionCreator';
import {ValidAgentNumber} from 'Users/models/AgentNumber';
import {Sort} from 'Shared/components/PagedView/Table/Sort';

export type FilterEnum = {
  name: string;
  value: string;
};

export enum AccountActionType {
  GET_ACCOUNTS = 'account/GET_ACCOUNTS',
  CHANGE_ACCOUNTS = 'account/CHANGE_ACCOUNTS',
  CHANGE_ACCOUNT_FILTERS = 'account/CHANGE_ACCOUNT_FILTERS',
  CHANGE_SORT = 'account/CHANGE_SORT',
  CLEAR_SORT = 'account/CLEAR_SORT',
  GET_POLICY_INFORMATION_REPORTS = 'account/GET_POLICY_INFORMATION_REPORTS',
  CHANGE_POLICY_INFORMATION_REPORTS = 'account/CHANGE_POLICY_INFORMATION_REPORTS',
  CHANGE_MAX_PAGE = 'account/CHANGE_MAX_PAGE',
  REMOVE_POLICY_INFORMATION_REPORTS = 'account/REMOVE_POLICY_INFORMATION_REPORTS',
}

export const EmptyAccountFilter: AccountFilters = {
  agentOfRecordNumber: '',
  lastAppSubmit: {
    endDate: '',
    startDate: '',
  },
  name: '',
  number: '',
  servicingAgentNumber: '',
  groupnumber: '',
  paymentStatuses: FilterOptionValue.EmptyOption(),
  status: FilterOptionValue.EmptyOption(),
};

export type AccountFilters = {
  agentOfRecordNumber: string;
  name: string;
  number: string;
  lastAppSubmit: DateRange;
  servicingAgentNumber: string;
  status: FilterOptionUnion;
  paymentStatuses: FilterOptionUnion;
  groupnumber: string;
};

export const changeAccountFilters = (filters: AccountFilters) =>
  createAction(AccountActionType.CHANGE_ACCOUNT_FILTERS, {filters});
  
export const getAccounts = (
  agentNumbers: ValidAgentNumber[],
  isNewYork: boolean,
  encryptedIdentifier: string,
  page?: Page<Account>,
  filter?: AccountFilters,
  sort?: Sort
) => createAction(AccountActionType.GET_ACCOUNTS, {page, filter, sort, agentNumbers, isNewYork, encryptedIdentifier});
export const changeSort = (sort?: Sort) => createAction(AccountActionType.CHANGE_SORT, {sort});
export const clearSort = () => createAction(AccountActionType.CLEAR_SORT);
export const changeAccounts = (accounts: AsyncPage<Page<Account>>) =>
  createAction(AccountActionType.CHANGE_ACCOUNTS, accounts);
export const clearAccounts = () => createAction(AccountActionType.CHANGE_ACCOUNTS, DEFAULT_STATE.accounts);
export const getPolicyInformationReports = (encryptedIdentifier: string, userId: string) =>
  createAction(AccountActionType.GET_POLICY_INFORMATION_REPORTS, {encryptedIdentifier, userId});
export const changePolicyInformationReports = (policyInformationReports: Async<AccountReportRequest[]>) =>
  createAction(AccountActionType.CHANGE_POLICY_INFORMATION_REPORTS, policyInformationReports);
export const changeMaxPage = (maxPage: boolean) =>
  createAction(AccountActionType.CHANGE_MAX_PAGE, maxPage);
export const deletePolicyInformationReport = (encryptedIdentifier: string, userId: string, reportId: number, eisFlag: boolean | null) =>
  createAction(AccountActionType.REMOVE_POLICY_INFORMATION_REPORTS, {encryptedIdentifier, userId, reportId, eisFlag});

type AccountState = {
  accounts: AsyncPage<Page<Account>>;
  filters: AccountFilters;
  sort: O.Option<Sort>;
  policyInformationReports: Async<AccountReportRequest[]>;
  maxPage: boolean;
};

export const DEFAULT_STATE: AccountState = {
  accounts: new Empty(),
  filters: EmptyAccountFilter,
  sort: O.none,
  policyInformationReports: new Empty(),
  maxPage: false,
};

type AccountAction =
  | ReturnType<typeof clearSort>
  | ReturnType<typeof changeSort>
  | ReturnType<typeof changeAccountFilters>
  | ReturnType<typeof changeAccounts>
  | ReturnType<typeof changePolicyInformationReports>
  | ReturnType<typeof changeMaxPage>;

export default (state: AccountState = DEFAULT_STATE, action: AccountAction): AccountState => {
  switch (action.type) {
    case AccountActionType.CLEAR_SORT:
      return {
        ...state,
        sort: DEFAULT_STATE.sort,
      };
    case AccountActionType.CHANGE_SORT:
      return {
        ...state,
        sort: O.fromNullable(action.payload.sort),
      };
    case AccountActionType.CHANGE_ACCOUNT_FILTERS:
      return {
        ...state,
        filters: action.payload.filters,
      };
    case AccountActionType.CHANGE_ACCOUNTS:
      return {
        ...state,
        accounts: action.payload,
      };
    case AccountActionType.CHANGE_POLICY_INFORMATION_REPORTS:
      return {
        ...state,
        policyInformationReports: action.payload,
      };
    case AccountActionType.CHANGE_MAX_PAGE:
      return {
        ...state,
        maxPage: action.payload,
      };
    default:
      return state;
  }
};
